import { useCallback, useRef, useState } from "react";
import { useAccount, useConfig } from "wagmi";
import {
  readContract,
  waitForTransactionReceipt,
  writeContract,
} from "wagmi/actions";
import { maxUint256 } from "viem";

import { api, ApiError, type Prepared, type VerifyResult } from "./api";
import { catalogAbi, erc20Abi, evoucherAbi, membershipAbi } from "./contracts";
import { runtimeConfig } from "./runtime-config";
import { describeWalletError } from "./errors";

/**
 * The payment flow, from plan §15.
 *
 * Seven steps, run in order, each reported so the stepper can show where it is
 * and where it stopped. The two that carry the design decisions:
 *
 *  - **Approve is conditional and exact-amount.** The allowance is read first
 *    and the approval is skipped when it already covers the payment, because a
 *    wallet popup nobody needed is the fastest way to make a flow feel broken.
 *    The amount approved is the payment, not `maxUint256`: an unlimited
 *    approval to a staging contract is a standing permission nobody revokes,
 *    and this app has no reason to ask for one.
 *
 *  - **Verification is what finishes, not the receipt.** A mined receipt means
 *    ASQ moved; it does not mean the membership exists. The flow is not done
 *    until the server has parsed the event and written the grant, which is
 *    plan §45 expressed as control flow.
 */

export type PaymentStep =
  | "idle"
  | "preparing"
  | "approving"
  | "signing"
  | "confirming"
  | "verifying"
  | "done"
  | "failed";

export type PaymentState = {
  step: PaymentStep;
  /** Set once the server has quoted, so the sheet can show the destination. */
  prepared: Prepared | null;
  transactionHash: `0x${string}` | null;
  /** Confirmations seen so far, for "2 of 3" rather than an endless spinner. */
  confirmations: number;
  required: number;
  result: VerifyResult | null;
  error: { code: string; message: string } | null;
};

const INITIAL: PaymentState = {
  step: "idle",
  prepared: null,
  transactionHash: null,
  confirmations: 0,
  required: runtimeConfig().confirmations,
  result: null,
  error: null,
};

export type PaymentTarget =
  | { utility: "ASETQU_MEMBERSHIP" }
  | { utility: "ASETQU_EVOUCHER"; voucherId: string }
  | { utility: "ASETQU_CATALOG"; productId: string };

export function usePayment() {
  const config = useConfig();
  const { address } = useAccount();
  const [state, setState] = useState<PaymentState>(INITIAL);

  // Guards against a second submission from a double-click or an impatient
  // retry. A second prepare would issue a second reference and a second order,
  // and the first would sit unpaid forever.
  const running = useRef(false);

  const reset = useCallback(() => {
    running.current = false;
    setState(INITIAL);
  }, []);

  const pay = useCallback(
    async (target: PaymentTarget) => {
      if (running.current) return;
      if (!address) {
        setState({
          ...INITIAL,
          step: "failed",
          error: { code: "NO_WALLET", message: "Hubungkan wallet terlebih dahulu." },
        });
        return;
      }

      running.current = true;
      setState({ ...INITIAL, step: "preparing" });

      try {
        // 1–3. The server checks the registry, the price and the balance, and
        // answers with a quote it will hold itself to.
        const prepared = await prepareFor(target, address);
        setState((current) => ({ ...current, prepared }));

        const amount = BigInt(prepared.amountRaw);
        const { asqToken } = runtimeConfig();

        // 4. Approve, only if the standing allowance is short.
        const allowance = await readContract(config, {
          address: asqToken,
          abi: erc20Abi,
          functionName: "allowance",
          args: [address, prepared.moduleAddress],
        });

        if (allowance < amount) {
          setState((current) => ({ ...current, step: "approving" }));
          const approvalHash = await writeContract(config, {
            address: asqToken,
            abi: erc20Abi,
            functionName: "approve",
            args: [prepared.moduleAddress, amount],
          });
          await waitForTransactionReceipt(config, { hash: approvalHash });
        }

        // 5. The payment itself.
        setState((current) => ({ ...current, step: "signing" }));
        const hash = await submitPayment(config, target, prepared);
        setState((current) => ({ ...current, step: "confirming", transactionHash: hash }));

        // 6. Wait for the chain. The server waits for more confirmations than
        // this does; the poll below is what actually gates the success screen.
        const receipt = await waitForTransactionReceipt(config, { hash });
        if (receipt.status !== "success") {
          throw new ApiError(409, "TRANSACTION_REVERTED",
            "Transaksi gagal on-chain. Tidak ada ASQ yang berpindah.");
        }

        // 7. Verification. Nothing is granted until this returns confirmed.
        setState((current) => ({ ...current, step: "verifying" }));
        const result = await pollVerification(hash, (progress) => {
          setState((current) => ({
            ...current,
            confirmations: progress.confirmations,
            required: progress.required || current.required,
          }));
        });

        setState((current) => ({ ...current, step: "done", result }));
      } catch (error) {
        setState((current) => ({ ...current, step: "failed", error: describeWalletError(error) }));
      } finally {
        running.current = false;
      }
    },
    [address, config],
  );

  return { state, pay, reset };
}

function prepareFor(target: PaymentTarget, wallet: `0x${string}`): Promise<Prepared> {
  switch (target.utility) {
    case "ASETQU_MEMBERSHIP":
      return api.prepareMembership(wallet);
    case "ASETQU_EVOUCHER":
      return api.prepareVoucher(target.voucherId, wallet);
    case "ASETQU_CATALOG":
      return api.prepareProduct(target.productId, wallet);
  }
}

/**
 * Builds and signs the module call.
 *
 * The module address comes from the quote, which came from the registry — never
 * from a constant in this bundle. That is what makes a deprecation take effect
 * without a redeploy of the SPA.
 */
function submitPayment(
  config: ReturnType<typeof useConfig>,
  target: PaymentTarget,
  prepared: Prepared,
): Promise<`0x${string}`> {
  const amount = BigInt(prepared.amountRaw);

  switch (target.utility) {
    case "ASETQU_MEMBERSHIP":
      // No amount argument: the price is an immutable on the contract.
      return writeContract(config, {
        address: prepared.moduleAddress,
        abi: membershipAbi,
        functionName: "activate",
        args: [prepared.referenceId],
      });

    case "ASETQU_EVOUCHER":
      return writeContract(config, {
        address: prepared.moduleAddress,
        abi: evoucherAbi,
        functionName: "redeem",
        args: [prepared.referenceId, requireItemId(prepared), amount],
      });

    case "ASETQU_CATALOG":
      return writeContract(config, {
        address: prepared.moduleAddress,
        abi: catalogAbi,
        functionName: "purchase",
        args: [prepared.referenceId, requireItemId(prepared), amount],
      });
  }
}

/**
 * The item hash the module records.
 *
 * Taken from the quote rather than recomputed here. Hashing the id in the
 * browser would work right up until the two implementations disagreed about
 * encoding, at which point the payment would settle against an item the server
 * cannot match — after the ASQ was gone.
 */
function requireItemId(prepared: Prepared): `0x${string}` {
  if (!prepared.itemId) {
    throw new ApiError(500, "MISSING_ITEM_ID",
      "Server tidak menyertakan item id pada payment ini.");
  }
  return prepared.itemId;
}

/**
 * Polls until the server confirms, or gives up loudly.
 *
 * The ceiling is real rather than infinite: on BSC testnet three confirmations
 * take seconds, and a poll that never ends turns a stuck node into a spinner
 * nobody can interpret. Giving up does not undo anything — the transaction is
 * mined and the tools page can verify it later — so the message says that
 * rather than implying the payment failed.
 */
async function pollVerification(
  hash: `0x${string}`,
  onProgress: (progress: { confirmations: number; required: number }) => void,
): Promise<VerifyResult> {
  const deadline = Date.now() + 180_000;

  for (;;) {
    const result = await api.verify(hash);

    if (result.status === "confirmed") return result;

    onProgress({ confirmations: result.confirmations, required: result.required });

    if (Date.now() > deadline) {
      throw new ApiError(504, "VERIFICATION_TIMEOUT",
        "Transaksi sudah masuk chain tetapi verifikasi belum selesai. " +
          "Buka Activity beberapa saat lagi — pembayaran tidak hilang.");
    }

    await new Promise((resolve) => setTimeout(resolve, 2_500));
  }
}

// Exported so a future "revoke approval" control has one place to read the
// sentinel from, rather than re-deriving what "unlimited" means.
export const UNLIMITED_ALLOWANCE = maxUint256;
